"use client";
import React from "react";
import { motion } from "framer-motion";

interface BackgroundToggleProps {
  backgroundMode: number; // 0 = splash, 1 = video, 2 = spline
  onToggle: () => void;
}

const BackgroundToggle: React.FC<BackgroundToggleProps> = ({
  backgroundMode,
  onToggle,
}) => {
  const getTooltip = () => {
    switch (backgroundMode) {
      case 0:
        return "Next: Video";
      case 1:
        return "Next: 3D Spline";
      case 2:
        return "Next: Splash";
      default:
        return "Switch Mode";
    }
  };

  const getIcon = () => {
    if (backgroundMode === 0) {
      // Current: Splash -> Next: Video icon
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-white transition-transform group-hover:scale-110"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    } else if (backgroundMode === 1) {
      // Current: Video -> Next: 3D cube icon
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-white transition-transform group-hover:scale-110"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      );
    } else {
      // Current: Spline -> Next: Cursor/Splash icon
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-white transition-transform group-hover:scale-110"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
          />
        </svg>
      );
    }
  };

  return (
    <motion.button
      onClick={onToggle}
      className="group absolute bottom-8 left-8 z-[100] flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/30 bg-white/20 shadow-lg backdrop-blur-md transition-all duration-300 hover:border-white/50 hover:bg-white/30"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.5, duration: 0.3 }}
      aria-label="Toggle background effect"
      data-blobity-tooltip={getTooltip()}
    >
      {getIcon()}
    </motion.button>
  );
};

export default BackgroundToggle;
